import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebase'
import { useAuth } from './AuthContext'

interface AdminContextType {
  isAdmin: boolean
  isLoading: boolean
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

// Hardcoded admin emails - add accountant's email here
const ADMIN_EMAILS = [
  'admin@cskaccounting.ca',
  'info@cskaccounting.ca',
  'azanka100@gmail.com',
]

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const { user, loading: authLoading } = useAuth()

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (authLoading) return

      if (!user) {
        setIsAdmin(false)
        setIsLoading(false)
        return
      }

      // Check if user email is in admin list
      const userEmail = user.email?.toLowerCase()
      if (userEmail && ADMIN_EMAILS.includes(userEmail)) {
        setIsAdmin(true)
        setIsLoading(false)
        return
      }

      // Also check Firestore config for dynamic admin list
      try {
        const configDoc = await getDoc(doc(db, 'config', 'admins'))
        if (configDoc.exists()) {
          const adminEmails: string[] = configDoc.data().emails || []
          if (userEmail && adminEmails.map(e => e.toLowerCase()).includes(userEmail)) {
            setIsAdmin(true)
            setIsLoading(false)
            return
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error)
      }

      setIsAdmin(false)
      setIsLoading(false)
    }

    checkAdminStatus()
  }, [user, authLoading])

  return (
    <AdminContext.Provider value={{ isAdmin, isLoading }}>
      {children}
    </AdminContext.Provider>
  )
}

export function useAdmin() {
  const context = useContext(AdminContext)
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}
